import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bell, BellOff, Loader2, Clock, Check } from "lucide-react";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

const DAY_LABELS = { 1: "L", 2: "M", 3: "X", 4: "J", 5: "V", 6: "S", 7: "D" };

export default function PushToggle() {
  const supported = typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window;

  const [permission, setPermission] = useState(supported ? Notification.permission : "denied");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  // Reminder settings (per-user schedule)
  const [settings, setSettings] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/me/reminders");
      setSettings(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const checkSubscribed = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) return;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    } catch { /* ignore */ }
  }, [supported]);

  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker.register("/sw.js").then(() => checkSubscribed()).catch(() => {});
  }, [supported, checkSubscribed]);

  const enable = async () => {
    if (!supported) {
      toast.error("Tu navegador no soporta notificaciones.");
      return;
    }
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Notificaciones no permitidas.");
        return;
      }
      const { data } = await api.get("/push/vapid-public-key");
      if (!data?.key) throw new Error("VAPID key ausente");
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.key),
      });
      await api.post("/push/subscribe", {
        subscription: sub.toJSON(),
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      toast.success("Notificaciones activadas. Recibirás recordatorios de fichaje.");
    } catch (err) {
      toast.error(err?.message || "No se pudieron activar las notificaciones.");
    } finally {
      setLoading(false);
    }
  };

  const disable = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        await api.delete("/push/unsubscribe", { data: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Notificaciones desactivadas.");
    } catch {
      toast.error("No se pudo desactivar.");
    } finally {
      setLoading(false);
    }
  };

  const sendTest = async () => {
    try {
      const { data } = await api.post("/push/test");
      toast.success(`Notificación de prueba enviada (${data.sent}/${data.total}).`);
    } catch {
      toast.error("No se pudo enviar la notificación de prueba.");
    }
  };

  const saveSchedule = async (next) => {
    setSavingSchedule(true);
    try {
      const { data } = await api.put("/me/reminders", next);
      setSettings(data);
      toast.success("Horario guardado");
      setScheduleOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar el horario");
    } finally {
      setSavingSchedule(false);
    }
  };

  if (!supported) return null;

  // Human-readable schedule summary
  const scheduleText = settings
    ? (() => {
        const days = settings.reminder_days || [];
        const isWeek = days.length === 5 && [1, 2, 3, 4, 5].every((d) => days.includes(d));
        const isAll = days.length === 7;
        const daysStr = isWeek ? "Lun-Vie" : isAll ? "Todos los días" : days.map((d) => DAY_LABELS[d]).join("");
        return `${daysStr} · ${settings.reminder_entry} entrada · ${settings.reminder_exit} salida`;
      })()
    : "Lun-Vie · 07:30 entrada · 15:30 salida";

  return (
    <div
      className="brutal-card p-4 flex flex-wrap items-center gap-3"
      data-testid="push-toggle"
    >
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${subscribed ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
        {subscribed ? <Bell size={20} /> : <BellOff size={20} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-extrabold text-base text-[#0B2A4A] leading-tight">
          Recordatorios de fichaje
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          {subscribed
            ? scheduleText
            : "Activa las notificaciones para recibir avisos"}
        </div>
      </div>
      {subscribed ? (
        <>
          <button
            type="button"
            onClick={() => setScheduleOpen(true)}
            data-testid="push-schedule-edit"
            className="h-10 px-3 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold hover:bg-slate-50 inline-flex items-center gap-1.5"
          >
            <Clock size={14} /> Horario
          </button>
          <button
            type="button"
            onClick={sendTest}
            data-testid="push-test"
            className="h-10 px-3 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold hover:bg-slate-50"
          >
            Probar
          </button>
          <button
            type="button"
            onClick={disable}
            disabled={loading}
            data-testid="push-disable"
            className="h-10 px-4 rounded-lg border border-rose-200 text-rose-600 text-xs font-semibold hover:bg-rose-50 inline-flex items-center gap-1.5"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <BellOff size={14} />}
            Desactivar
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={enable}
          disabled={loading || permission === "denied"}
          data-testid="push-enable"
          className="h-10 px-4 rounded-lg bg-gradient-to-br from-[#FF8A1F] to-[#F5A623] text-white text-xs font-semibold inline-flex items-center gap-1.5 hover:shadow-lg disabled:opacity-60"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />}
          {permission === "denied" ? "Bloqueadas" : "Activar"}
        </button>
      )}

      {scheduleOpen && settings && (
        <ScheduleModal
          initial={settings}
          onClose={() => setScheduleOpen(false)}
          onSave={saveSchedule}
          saving={savingSchedule}
        />
      )}
    </div>
  );
}

function ScheduleModal({ initial, onClose, onSave, saving }) {
  const [entry, setEntry] = useState(initial.reminder_entry || "07:30");
  const [exit, setExit] = useState(initial.reminder_exit || "15:30");
  const [days, setDays] = useState(initial.reminder_days || [1, 2, 3, 4, 5]);
  const [enabled, setEnabled] = useState(initial.reminders_enabled !== false);

  const toggleDay = (d) => {
    setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort()));
  };
  const submit = (e) => {
    e.preventDefault();
    if (!days.length) return;
    onSave({
      reminder_entry: entry,
      reminder_exit: exit,
      reminder_days: days,
      reminders_enabled: enabled,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="reminders-modal"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h3 className="font-display font-black text-xl text-[#0B2A4A]">Horario de recordatorios</h3>
          <p className="text-xs text-slate-500 mt-1">
            Elige cuándo quieres recibir avisos para fichar. Hora Islas Canarias.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <label className="flex items-center gap-3 select-none">
            <input
              type="checkbox"
              data-testid="reminders-enabled"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="w-5 h-5 accent-[#FF4500]"
            />
            <span className="text-sm font-semibold">Recordatorios activos</span>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-up block mb-1.5">Entrada</label>
              <input
                type="time"
                data-testid="reminder-entry"
                value={entry}
                onChange={(e) => setEntry(e.target.value)}
                className="brutal-input w-full"
                required
              />
            </div>
            <div>
              <label className="label-up block mb-1.5">Salida</label>
              <input
                type="time"
                data-testid="reminder-exit"
                value={exit}
                onChange={(e) => setExit(e.target.value)}
                className="brutal-input w-full"
                required
              />
            </div>
          </div>

          <div>
            <label className="label-up block mb-2">Días</label>
            <div className="flex gap-1.5 justify-between">
              {[1, 2, 3, 4, 5, 6, 7].map((d) => {
                const on = days.includes(d);
                return (
                  <button
                    type="button"
                    key={d}
                    data-testid={`day-${d}`}
                    onClick={() => toggleDay(d)}
                    className={`flex-1 h-11 rounded-lg font-bold text-sm transition-colors ${
                      on
                        ? "bg-[#FF4500] text-white shadow-md"
                        : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                    }`}
                  >
                    {DAY_LABELS[d]}
                  </button>
                );
              })}
            </div>
            {!days.length && (
              <p className="text-xs text-rose-600 mt-1.5">Selecciona al menos un día.</p>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-11 rounded-lg border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || !days.length}
              data-testid="reminders-save"
              className="flex-1 h-11 rounded-lg bg-[#0B2A4A] text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
